/**
 * HeaderAnalyticsFunction.ts
 * Common analytics functions for header components
 */

import {
  dlEventConstants,
  componentNameConstants,
  dlElementConstants,
  headerConstants,
} from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";

/**
 * Handle notification delete click analytics
 * Logs click event for the notification delete icon
 */
export const handleHeaderNotificationDeleteDL = (cta_text: string) => {
  const data = {
    component: componentNameConstants.HEADER_NOTIFICATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.ICON_CLICK,
    element: dlElementConstants.NOTIFICATION_DELETE_ICON,
    cta_text,
  };

  const cleverTapObj = {
    EVENT_NAME: headerConstants.EVENT_NAMES.NOTIFICATION_DELETE_ICON,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle notification bell icon click analytics
 * Logs click event for the notification bell icon in the header
 */
export const handleHeaderNotificationBellDL = (cta_text: string) => {
  const data = {
    component: componentNameConstants.HEADER_NOTIFICATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.ICON_CLICK,
    element: dlElementConstants.NOTIFICATION_BELL_ICON,
    cta_text,
  };

  const cleverTapObj = {
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    element: data.element,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle notification close icon click analytics
 * Logs click event for the notification panel close icon
 */
export const handleHeaderNotificationCloseDL = (
  cta_text: string = headerConstants.CTA_TEXT.NOTIFICATION_CLOSE,
) => {
  const data = {
    component: componentNameConstants.HEADER_NOTIFICATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.ICON_CLICK,
    element: headerConstants.ELEMENTS.NOTIFICATION_CLOSE_ICON,
    cta_text,
  };

  const cleverTapObj = {
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    element: data.element,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle header sign in click analytics
 * Logs click event for the sign in button/icon in header
 */
export const handleHeaderSignInClickDL = (
  cta_text: string = headerConstants.CTA_TEXT.HEADER_SIGN_IN,
) => {
  const data = {
    component: componentNameConstants.HeaderSignIn,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.HEADER_CLICK,
    element: headerConstants.ELEMENTS.HEADER_SIGN_IN_ICON,
    cta_text,
  };

  const cleverTapObj = {
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    element: data.element,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle EMI card click analytics in header
 * Logs click event for the EMI card in header
 */
export const handleHeaderEmiCardClickDL = (
  cta_text: string = headerConstants.CTA_TEXT.EMI_CARD,
) => {
  const data = {
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.HEADER_CLICK,
    component: headerConstants.COMPONENTS.HEADER,
    cta_text,
    section_title: headerConstants.SECTION_TITLES.HEADER,
    element: headerConstants.ELEMENTS.HEADER_EMI_ICON,
  };

  const cleverTapObj = {
    event: data.event,
    event_type: data.event_type,
    component: data.component,
    cta_text: data.cta_text,
    section_title: data.section_title,
    element: data.element,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle cart icon click analytics in header
 * Logs click event for the cart icon in header
 */
export const handleHeaderCartClickDL = (
  destinationUrl: string,
  cta_text: string = headerConstants.CTA_TEXT.HEADER_CART,
) => {
  const data = {
    element: headerConstants.ELEMENTS.HEADER_CART_ICON,
    component: headerConstants.COMPONENTS.HEADER_CART,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.HEADER_CLICK,
    destination_url: destinationUrl,
    cta_text,
    section_title: headerConstants.SECTION_TITLES.HEADER_CART,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    destination_url: data.destination_url,
    cta_text: data.cta_text,
    section_title: data.section_title,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle rewards/prime icon click analytics in header
 * Logs click event for the rewards/prime icon in header
 */
export const handleHeaderRewardsClickDL = (
  destinationUrl: string,
  cta_text: string = headerConstants.CTA_TEXT.PRIME,
) => {
  const data = {
    element: headerConstants.ELEMENTS.HEADER_REWARDS_ICON,
    component: headerConstants.COMPONENTS.HEADER_REWARD,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.HEADER_CLICK,
    destination_url: destinationUrl,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    destination_url: data.destination_url,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle partner icon click analytics in header
 * Logs click event for the partner icon in header
 */
export const handleHeaderPartnerClickDL = (
  cta_text: string = headerConstants.CTA_TEXT.PARTNERS,
) => {
  const data = {
    element: headerConstants.ELEMENTS.HEADER_PARTNER_ICON,
    component: headerConstants.COMPONENTS.HEADER_PARTNER,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.HEADER_CLICK,
    destination_url: "", // Partner button doesn't have href, it's a dropdown
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    destination_url: data.destination_url,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle header menu button click analytics
 * Logs click event for the header menu/sidebar button
 */
export const handleHeaderMenuClickDL = (
  cta_text: string = headerConstants.CTA_TEXT.MENU,
) => {
  const data = {
    element: headerConstants.ELEMENTS.TOP_NAVIGATION_SIDEBAR,
    component: headerConstants.COMPONENTS.TOP_NAVIGATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.HEADER_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle header menu close button click analytics
 * Logs click event for the close (X) button in the sidebar menu
 */
export const handleHeaderMenuCloseClickDL = (
  cta_text: string = headerConstants.CTA_TEXT.CLOSE_MENU,
) => {
  const data = {
    element: headerConstants.ELEMENTS.TOP_NAVIGATION_SIDEBAR,
    component: headerConstants.COMPONENTS.TOP_NAVIGATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.LINK_CLICK,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle main menu item click analytics
 * Logs click event for main menu items in the sidebar
 */
export const handleHeaderMainMenuClickDL = (
  menuTitle: string,
  source: string = "main",
) => {
  const data = {
    element: headerConstants.ELEMENTS.TOP_NAVIGATION_SIDEBAR,
    component: headerConstants.COMPONENTS.TOP_NAVIGATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.LINK_CLICK,
    cta_text: menuTitle,
    menu_source: source,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    cta_text: data.cta_text,
    menu_source: data.menu_source,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle sidebar sign in/profile link click analytics
 * Logs click event for the sign in or profile link in the sidebar
 */
export const handleHeaderSidebarProfileClickDL = (
  url: string,
  cta_text: string,
) => {
  const data = {
    element: headerConstants.ELEMENTS.TOP_NAVIGATION_SIDEBAR,
    component: headerConstants.COMPONENTS.TOP_NAVIGATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.LINK_CLICK,
    destination_url: url,
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    destination_url: data.destination_url,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle sidebar main menu button click analytics
 * Logs click event for main menu buttons in the sidebar
 */
export const handleHeaderSidebarMenuButtonClickDL = (cta_text: string) => {
  const data = {
    element: headerConstants.ELEMENTS.TOP_NAVIGATION_SIDEBAR,
    component: headerConstants.COMPONENTS.TOP_NAVIGATION,
    event: dlEventConstants.CLICK,
    event_type: dlEventConstants.LINK_CLICK,
    destination_url: "", // Menu buttons don't have direct URLs
    cta_text,
  };

  const cleverTapObj = {
    element: data.element,
    component: data.component,
    event: data.event,
    event_type: data.event_type,
    destination_url: data.destination_url,
    cta_text: data.cta_text,
  };

  dataLayer("", data, cleverTapObj);
};
